class ConsentManager {
  constructor(siteId) {
    this.siteId = siteId;
    this.currentConsent = this.getConsent();
    this.initListeners();
  }

  // Read and parse the Iubenda cookie
  getConsent() {
    const name = `_iub_cs-${this.siteId}=`;
    const decoded = decodeURIComponent(document.cookie);
    const parts = decoded.split(";");
    for (let part of parts) {
      part = part.trim();
      if (part.startsWith(name)) {
        try {
          return JSON.parse(part.substring(name.length));
        } catch (e) {
          console.warn("Failed to parse Iubenda cookie:", e);
        }
      }
    }
    return null;
  }

  // Listen for changes
  initListeners() {
    [
      "iubenda_consent_ready",
      "iubenda_consent_updated",
      "iubenda_consent_given",
      "iubenda_consent_rejected",
      "iubenda_consent_first_given"
    ].forEach((evt) => {
      document.addEventListener(evt, () => {
        this.currentConsent = this.getConsent();
        console.log(`Consent change detected (${evt}):`, this.currentConsent);
        this.onConsentChange(this.currentConsent);
      });
    });
  }

  onConsentChange(consent, evt) {
    if (this.changeCallback) {
      this.changeCallback(consent);
    }
  }

  // Utility method to check current consent
  hasAnalyticsConsent() {
    return this.currentConsent?.purposes?.["4"] === true;
  }

  onChange(callback) {
    this.changeCallback = callback;
  }
}
class AnalyticsService {
  constructor(consentManager) {
    this.isBrowser = typeof window !== "undefined";
    this.device = window.innerWidth < 768 ? "mobile" : "desktop";
    this.deviceSuffix = this.device === "mobile" ? "_mobile" : "";
    this.pageType = this.getPageType();
    this.consentManager = consentManager;
    this.enabled = false;
  }

  init() {
    if (!this.isBrowser) return;

    // On load: check existing consent
    if (this.consentManager.hasAnalyticsConsent()) {
      this.enableAnalytics();
    } else {
      console.log("Analytics disabled until consent is given.");
    }

    // React to future consent updates
    this.consentManager.onChange((consent) => {
      if (consent.purposes["4"] && !this.enabled) {
        console.log("Consent now given → enabling analytics");
        this.enableAnalytics();
      } else if (!consent.purposes["4"] && this.enabled) {
        console.log("Consent revoked → disabling analytics");
        this.disableAnalytics();
      }
    });
  }

  enableAnalytics() {
    this.initDataLayer();
    this.initGTM("GTM-P88R68");
    this.registerBasedOnPageType();
    this.enabled = true;
  }

  disableAnalytics() {
    this.enabled = false;
    // optionally clear dataLayer or disable custom listeners
    console.log("Analytics tracking disabled");
  }

  // ---- your existing code below ----
  debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  }

  throttle(func, delay) {
    let lastCall = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  }

  initDataLayer() {
    if (this.isBrowser && !window.dataLayer) {
      window.dataLayer = [];
    }
  }

  initGTM(containerId) {
    if (!this.isBrowser) return;

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const f = document.getElementsByTagName("script")[0];
    const j = document.createElement("script");
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    f.parentNode.insertBefore(j, f);
  }

  getPageType() {
    const template = document.body.dataset.template;
    const templateSuffix = document.body.dataset.templateSuffix || "";
    switch (template) {
      case "index":
        return "homepage";
      case "product":
        return "products";
      case "collection":
        return "collections";
      case "page":
        if (templateSuffix.includes("lookbook")) return "lookbook";
        if (templateSuffix.includes("film")) return "film";
        if (templateSuffix.includes("radio")) return "radio";
        return "pages";
      case "account":
      case "register":
      case "login":
        return "account";
      default:
        return "global";
    }
  }

  registerBasedOnPageType() {
    this.register(this.pageType);
  }

  register(pageType) {
    const eventMap = {
      global: {
        pageView: this.pageView,
        topBannerClick: this.topBannerClick,
        navHover: this.navHover,
        mobileNavOpen: this.mobileNavOpen,
        topNavClick: this.topNavClick,
        secondTierNavClick: this.secondTierNavClick,
        thirdTierNavClick: this.thirdTierNavClick,
        // Search
        searchIconClick: this.searchIconClick,
        searchResultClick: this.searchResultClick,
        searchSuggestedClick: this.searchSuggestedClick,
        searchResultsView: this.searchResultsView,
        searchCloseResults: this.searchCloseResults,
        // Footer
        footerView: this.footerView,
        footerClick: this.footerClick,
        // Scroll
        pageScrollDepth: this.pageScrollDepth,
        // Newsletter
        newsletterImpression: this.newsletterImpression,
        newsletterSignup: this.newsletterSignup,
        // Cart
        headerViewCart: this.headerViewCart,
      },
      homepage: {
        homeHeroClick: this.homeHeroClick,
        homeSectionView: this.homeSectionView,
        homeSectionClick: this.homeSectionClick,
      },
      collections: {
        viewItemList: this.viewItemList,
        selectItem: this.selectItem,
        plpNavClick: this.plpNavClick,
        plpScroll: this.plpScroll,
        plpSelectSize: this.plpSelectSize,
        previewSwipeItem: this.previewSwipeItem,
        previewEngagedSwipeItem: this.previewEngagedSwipeItem,
        previewSwipeAndClickItem: this.previewSwipeAndClickItem,
        addToCart: this.addToCart,
        customBlockView: this.customBlockView,
        customBlockClick: this.customBlockClick,
      },
      products: {
        selectSize: this.selectSize,
        breadcrumbClick: this.breadcrumbClick,
        addToCart: this.addToCart,
        viewCart: this.viewCart,
        externalLinkClick: this.externalLinkClick,
        familyVariantClick: this.familyVariantClick,
        PDPSectionEvent: this.PDPSectionEvent,
        productAccordionClick: this.productAccordionClick,
        sizeGuideClick: this.sizeGuideClick,
        selectColor: this.selectColor,
      },
      pages: {
        contactSubmit: this.contactSubmit,
        newsletterSignup: this.newsletterSignup,
      },
      account: {
        loginSuccess: this.loginSuccess,
        signupSuccess: this.signupSuccess,
      },
    };

    const combinedEvents = {
      ...(eventMap.global || {}),
      ...(eventMap[pageType] || {}),
    };

    for (const [eventName, handler] of Object.entries(combinedEvents)) {
      document.addEventListener(`analytics:${eventName}`, (e) => {
        const args = Array.isArray(e.detail) ? e.detail : [e.detail];
        handler.call(this, ...args);
      });
    }
  }

  trackEvent(event, payload) {
    if (!this.isBrowser) return;

    // Check if analytics is enabled globally
    if (!this.enabled) {
      console.log(`Skipping event "${event}" – analytics disabled`);
      return;
    }

    if (window.dataLayer) {
      console.log("Tracking event:", event, payload);
      window.dataLayer.push({ event, ...payload });
    }
  }

  // HELPERS
  buildAnalyticsEvent(eventName, detail) {
    return new CustomEvent(eventName, { detail });
  }

  getCurrency() {
    return (
      document.querySelector("div[data-currency]")?.dataset.currency || "EUR"
    );
  }

  buildEcommerceData(products, currency) {
    let mappedProducts = products.map((el, i) => {
      const dataset = el.dataset;
      return {
        item_id: dataset.productId,
        item_name: dataset.productName,
        item_brand: "Aeyde",
        item_category: dataset.productType,
        price: parseFloat(dataset.productPrice),
        index: i,
      };
    });
    return { currency, items: mappedProducts };
  }

  // GLOBAL
  topBannerClick(payload) {
    this.trackEvent("banner_click", payload);
  }
  headerViewCart(payload) {
    this.trackEvent("view_cart", payload);
  }
  // NAVIGATION
  navHover(payload) {
    this.trackEvent("nav_hover", payload);
  }
  mobileNavOpen(payload) {
    this.trackEvent("mobile_nav_open", payload);
  }
  topNavClick(payload) {
    this.trackEvent("top_nav_click", payload);
  }
  secondTierNavClick(payload) {
    this.trackEvent("second_tier_nav_click", payload);
  }
  thirdTierNavClick(payload) {
    this.trackEvent("third_tier_nav_click", payload);
  }
  // SEARCH
  searchIconClick(payload) {
    this.trackEvent("begin_search", payload);
  }
  searchSuggestedClick(payload) {
    this.trackEvent("click_search_element", payload);
  }
  searchResultClick(payload) {
    this.trackEvent("click_search_results", payload);
  }
  searchResultsView(payload) {
    this.trackEvent("view_search_results", payload);
  }
  searchCloseResults(payload) {
    this.trackEvent("close_search_results", payload);
  }
  // FOOTER
  footerView(payload) {
    this.trackEvent("footer_view", payload);
  }
  footerClick(payload) {
    this.trackEvent("footer_click", payload);
  }
  // SCROLL
  pageScrollDepth(payload) {
    this.trackEvent("scroll", payload);
  }
  // NEWSLETTER
  newsletterImpression(payload) {
    this.trackEvent("newsletter_impression", payload);
  }

  newsletterSignup(payload) {
    this.trackEvent("newsletter_signup", payload);
  }
  // HOMEPAGE
  homeHeroClick(payload) {
    this.trackEvent("homepage_hero_image_click", payload);
  }
  homeSectionClick(payload) {
    if (payload.content_section_type == "gallery_section") {
      this.trackEvent("homepage_gallery_slider_click", payload);
    } else {
      const eventName = `homepage_${
        payload.content_section_type == "link_cards" ? "product" : "media"
      }_${payload.content_section_index}_click`;
      this.trackEvent(eventName, payload);
    }
  }
  homeSectionView(payload) {
    if (payload.content_section_type == "gallery_section") {
      this.trackEvent("homepage_gallery_slider_view", payload);
    } else {
      const eventName = `homepage_${
        payload.content_section_type == "link_cards" ? "product" : "media"
      }_${payload.content_section_index}_view`;
      this.trackEvent(eventName, payload);
    }
  }

  // PLP
  viewItemList(payload) {
    this.trackEvent("view_item_list", payload);
  }
  selectItem(payload) {
    this.trackEvent("select_item", payload);
  }
  plpNavClick(payload) {
    this.trackEvent("plp_nav_click", payload);
  }
  plpScroll(payload) {
    this.trackEvent("plp_scroll", payload);
  }
  plpSelectSize(payload) {
    this.trackEvent("plp_select_size", payload);
  }
  previewSwipeItem(payload) {
    this.trackEvent("plp_preview_swipe", payload);
  }
  previewEngagedSwipeItem(payload) {
    this.trackEvent("plp_preview_engaged_swipe", payload);
  }
  previewSwipeAndClickItem(payload) {
    this.trackEvent("plp_preview_swipe_and_click", payload);
  }
  customBlockView(payload) {
    this.trackEvent("custom_block_view", payload);
  }
  customBlockClick(payload) {
    this.trackEvent("custom_block_click", payload);
  }

  // PDP
  selectSize(payload) {
    this.trackEvent("select_size", payload);
  }
  breadcrumbClick(payload) {
    this.trackEvent("breadcrumb_click", payload);
  }
  addToCart(payload) {
    this.trackEvent("add_to_cart", payload);
  }
  viewCart(payload) {
    this.trackEvent("instant_view_cart", payload);
  }
  externalLinkClick(payload) {
    this.trackEvent("pdp_content_outbound_click", payload);
  }
  familyVariantClick(payload) {
    this.trackEvent("family_variant_click", payload);
  }
  PDPSectionEvent(payload) {
    this.trackEvent(payload.event_name, payload);
  }
  productAccordionClick(payload) {
    this.trackEvent("product_accordion_click", payload);
  }
  sizeGuideClick(payload) {
    this.trackEvent("size_guide_click", payload);
  }
  selectColor(payload) {
    this.trackEvent("select_color", payload);
  }

  // PAGES

  // ACCOUNT
  signupSuccess(payload) {
    this.trackEvent("signup", payload);
  }
  loginSuccess(payload) {
    this.trackEvent("login", payload);
  }
}
console.log("Initializing Consent & Analytics");

const consentManager = new ConsentManager(58894851);

consentManager.onChange((consent) => {
  if (consent?.purposes?.["4"]) {
    console.log("✅ Analytics consent granted");
    window.AnalyticsService.enableAnalytics();
  } else {
    console.log("❌ Analytics consent revoked");
    window.AnalyticsService.disableAnalytics();
  }
});
const analyticsService = new AnalyticsService(consentManager);

window.AnalyticsService = analyticsService;
analyticsService.init();
